import {
  Button as MaterialButton,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link as MaterialLink,
  makeStyles,
  Paper,
  Tooltip,
} from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import AddIcon from '@material-ui/icons/Add';
import ArchiveIcon from '@material-ui/icons/Archive';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import SearchIcon from '@material-ui/icons/Search';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import cn from 'classnames';
import { parse as parseQuery, stringify as stringifyQuery } from 'query-string';
import React, {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useHistory, useLocation } from 'react-router';
import { Column, Table } from 'react-virtualized';
import _pick from 'lodash/pick';
import _uniq from 'lodash/uniq';
import foundersAPI, { Founder } from '../../api/founders';
import { Role } from '../../api/user/Role';
import { VentureId } from '../../api/ventures/types/Venture';
import { useFoundersContext } from '../../contexts/founders-context';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { urlfy } from '../../utils/String/Url';
import {
  checkSizeTablet,
  decodeQuery,
  encodeQuery,
  getColorStatus,
  isMobile,
  isTablet,
} from '../../utils/functions';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import {
  AlertState,
  Avatar,
  ButtonsChangeStatusFounder,
  ConfirmButton,
  CopyText,
  Link,
  Modal,
  Select,
  SelectVenturePopover,
  StatusBadge,
  Text,
  TextField,
} from '../common';
import AboutIcon from '../common/about-icon';
import {
  TableCell,
  TableFooter,
  TableHeadCell,
  TableHeadRow,
  TableRow,
} from '../common/table';
import FoundersRowActions from '../founders/founders-row-actions';

const filters = {
  status: [
    {
      label: 'All Statuses',
      value: 'none',
    },
    {
      label: 'Active',
      value: 'Active',
    },
    {
      label: 'Archived',
      value: 'Archived',
    },
    {
      label: 'Applicant',
      value: 'Applicant',
    },
    {
      label: 'Rejected',
      value: 'Rejected',
    },
  ],
};

type FilterValueType =
  | {
    key: 'search' | 'status' | 'venture';
    value: string;
  }
  | undefined;

type DecodedVentureFilter = {
  id: VentureId;
  ventureName: string;
};

const useStyles = makeStyles({
  container: {
    padding: 0,
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 12px 12px 16px',
    height: 64,
    boxSizing: 'border-box',
  },
  filterList: {
    display: 'flex',
    alignItems: 'center',
  },
  filterItem: {
    '& + &': {
      marginLeft: 15,
    },
  },
  filterSelect: {
    width: 160,
  },
  ventureInput: {
    width: 180,
  },
  filterActions: {
    display: 'flex',
    alignItems: 'center',
    margin: '-1px 0',
  },
  filterActionsAssignArchived: {
    marginLeft: 11,
  },
  filterActionsAssignUnArchive: {
    marginLeft: 2,
  },
  emptyBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 696,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 500,
  },
  emptyStateBtn: {
    marginTop: 16,
  },
  table: {
    outline: 'none',
  },
  column: {
    display: 'flex',
  },
  row: {
    '&:hover $actionsCell': {
      display: 'flex',
    },
  },
  linkedinButton: {
    padding: 10,
  },
  linkedinIcon: {
    color: '#0A66C2',
    fontSize: 20,
  },
  nameColumn: {
    paddingRight: 10,
    boxSizing: 'border-box',
  },
  assignedColumn: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '0 32px 0 10px',
    boxSizing: 'border-box',
    textAlign: 'right',
  },
  emailColumn: {
    paddingRight: 20,
    boxSizing: 'border-box',
  },
  actionsCell: {
    display: 'none',
    alignItems: 'center',
  },
  searchInput: {
    width: '100%',
    maxWidth: 320,
  },
  justifyContentEnd: {
    justifyContent: 'flex-end',
  },
  containerRemoveFilter: {
    marginLeft: 10,
  },
  iconRemoveFilter: {
    width: 20,
  },
  iconCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    paddingRight: 32,
  },
});

function getFilterItemByFilter(filter: {
  key: 'search' | 'status' | 'venture';
  value: string;
}) {
  const keyAliases = {
    search: 'Search',
    status: 'Status',
    venture: 'Venture',
  };
  return {
    label: keyAliases[filter.key],
    value:
      filter.key === 'search'
        ? filter.value
        : filter.key === 'venture'
          ? (decodeQuery(filter.value) as DecodedVentureFilter)?.ventureName
          : filters[filter.key].find(
            (filterVal) => filterVal.value === filter.value,
          )?.label,
  };
}

function parseFilterFromURL(search: string): FilterValueType {
  const parsedFilter = _pick(parseQuery(search), [
    'search',
    'status',
    'venture',
  ]);
  const filterName = Object.keys(parsedFilter)[0] as
    | 'search'
    | 'status'
    | 'venture';
  const filterValue = parsedFilter[filterName as keyof typeof parsedFilter];
  return typeof filterValue === 'string'
    ? { key: filterName, value: filterValue }
    : undefined;
}

function getPageFromURL(search: string) {
  const parsedQuery = _pick(parseQuery(search), ['page']);
  return parsedQuery.page ? Number(parsedQuery.page) : 1;
}

async function foundersRequest(filter: FilterValueType, page: number) {
  if (filter) {
    let loadedFounders;
    switch (filter.key) {
      case 'search':
        loadedFounders = await foundersAPI.search(filter.value);
        break;
      case 'status':
        loadedFounders = await foundersAPI.getFoundersByStatus(
          filter.value,
          page,
        );
        break;
      case 'venture':
        const decodedVentureFilter = decodeQuery(
          filter.value,
        ) as DecodedVentureFilter;
        loadedFounders = await foundersAPI.getFoundersDetailsByVentureId(
          decodedVentureFilter.id,
        );
        break;
      default:
        return [];
    }
    return loadedFounders;
  }
  return foundersAPI.getFounders(page);
}

function stopPropagation(e: SyntheticEvent<any>) {
  e.stopPropagation();
}

function getDataTestId(data: string, isActive: boolean): string {
  return `${data}${isActive ? '-active' : ''}`;
}

function FoundersTable() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { width: containerWidth, ref: containerRef } = useResizeDetector();

  const { hasAccessToAction, hasRole } = useContext(UserContext);
  const {
    founders,
    foundersLogos,
    selectedFounderIds,
    isSendingProfileUpdate,
    resetKey,
    setFounders,
    setSelectedFounderIds,
    sendFounderUpdateProfilesSelected,
    setArchiveTargetFounders,
  } = useFoundersContext();

  const currentPath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location],
  );

  const filterValue = useMemo(
    () => parseFilterFromURL(location.search),
    [location.search],
  );

  const debouncedTimer = useRef<number>();
  const [searchValue, setSearchValue] = useState(
    filterValue?.key === 'search' ? filterValue.value : '',
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadError, setLoadError] = useState();
  const [isEmptyState, setIsEmptyState] = useState(false);

  const page = useMemo(
    () => getPageFromURL(location.search),
    [location.search],
  );

  const perPage = useMemo(
    () =>
      filterValue?.key !== 'search' && filterValue?.key !== 'venture'
        ? 10
        : undefined,
    [filterValue],
  );

  const statusSelectedFounders = useMemo(() => {
    if (!selectedFounderIds) {
      return undefined;
    }
    const selectedFounders = founders.filter((founder) =>
      selectedFounderIds.includes(founder.id),
    );

    const activeSelectedFounders = selectedFounders.filter(
      (founder) => founder.status === 'Active',
    );

    const archivedSelectedFounders = selectedFounders.filter(
      (founder) => founder.status === 'Archived',
    );

    let disabledButton;
    if (
      activeSelectedFounders.length > 0 &&
      archivedSelectedFounders.length === 0
    ) {
      disabledButton = 'active';
    }
    if (
      archivedSelectedFounders.length > 0 &&
      activeSelectedFounders.length === 0
    ) {
      disabledButton = 'archived';
    }

    return {
      activeSelectedFounders,
      archivedSelectedFounders,
      disabledButton,
    };
  }, [selectedFounderIds, founders]);

  const showClearFilter = useMemo(() => {
    return !!filterValue || !!searchValue;
  }, [filterValue, searchValue]);

  const previousResetKey = useRef(resetKey);

  const handlePageChange = useCallback(
    (nextPage) => {
      const query = stringifyQuery({
        page: nextPage > 1 ? nextPage : undefined,
        ...(filterValue
          ? {
            [filterValue.key]: filterValue.value,
          }
          : {}),
      });

      if (query) {
        history.replace(`${location.pathname}?${query}`);
      } else {
        history.replace(location.pathname);
      }
    },
    [filterValue, location.pathname, history],
  );

  const loadFounders = useCallback(
    async (
      filter: FilterValueType,
      currentPage: number,
      isReload?: boolean,
    ) => {
      try {
        setFounders([]);
        setSelectedFounderIds([]);
        setIsLoading(true);
        setIsEmptyState(false);
        if (filter?.key !== 'search' && searchValue.length > 2) {
          setSearchValue('');
        }
        const loadedFounders = await foundersRequest(filter, currentPage - 1);
        setFounders(loadedFounders);
        setIsLoading(false);

        if (loadedFounders.length === 10) {
          setIsNextPageLoading(true);
          try {
            const nextFounders = await foundersRequest(filter, currentPage);
            setIsNextPageLoading(false);
            setHasNextPage(nextFounders.length > 0);
          } catch (e: any) {
            setIsNextPageLoading(false);
            setHasNextPage(false);
          }
        } else {
          setHasNextPage(false);
        }
      } catch (e: any) {
        if (e?.response?.status === 404) {
          if (isReload && currentPage > 1) {
            handlePageChange(currentPage - 1);
            return;
          } else if (currentPage > 1) {
            handlePageChange(1);
            return;
          }
          setIsLoading(false);
          return setIsEmptyState(true);
        }
        const errorMessage =
          e?.response?.data?.message || 'Internal server error';
        setIsLoading(false);
        setLoadError(errorMessage);
      }
    },
    [setFounders, setSelectedFounderIds, searchValue, handlePageChange],
  );

  const handleFilterUpdate = useCallback(
    (field, value) => {
      if (!value || value === 'none') {
        history.replace(location.pathname);
      } else {
        history.replace(
          `${location.pathname}?${stringifyQuery({ [field]: value })}`,
        );
      }
    },
    [history, location.pathname],
  );

  const handleClearFilters = useCallback(() => {
    if (searchValue) {
      setSearchValue('');
    }
    history.replace(location.pathname);
  }, [history, location, searchValue]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      clearTimeout(debouncedTimer.current);
      setSearchValue(e.target.value);

      debouncedTimer.current = setTimeout(() => {
        if (e.target.value.trim().length > 2) {
          handleFilterUpdate('search', e.target.value.trim());
        } else {
          handleFilterUpdate('search', null);
        }
      }, 800) as unknown as number;
    },
    [handleFilterUpdate],
  );



  const handleAllRowSelect = useCallback(
    (checked: boolean) => {
      if (checked) {
        const foundersIds = founders.map((founder) => founder.id);
        return setSelectedFounderIds([...foundersIds]);
      }
      return setSelectedFounderIds([]);
    },
    [founders, setSelectedFounderIds],
  );

  const handleRowSelect = useCallback(
    (checked: boolean, founderId: Founder['id']) => {
      if (checked) {
        return setSelectedFounderIds(_uniq([...selectedFounderIds, founderId]));
      }
      return setSelectedFounderIds(
        selectedFounderIds.filter((id) => id !== founderId),
      );
    },
    [selectedFounderIds, setSelectedFounderIds],
  );

  const handleRowClick = useCallback(
    ({ rowData }) => {
      const founderId = rowData.id;
      handleRowSelect(!selectedFounderIds.includes(founderId), founderId);
    },
    [selectedFounderIds, handleRowSelect],
  );

  useEffect(() => {
    let isReload = false;
    if (previousResetKey.current !== resetKey) {
      isReload = true;
      previousResetKey.current = resetKey;
    }
    loadFounders(filterValue, page, isReload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, page, resetKey]);

  const isSmallTablet = checkSizeTablet(900);

  return (
    <Paper className={classes.container} data-testid='founders-table'>
      {hasAccessToAction('founder.filters.view') && (
        <div className={classes.filterContainer}>
          <div className={classes.filterList}>
            {hasAccessToAction('founder.filters.search') && (
              <div
                className={classes.filterItem}
                data-testid={getDataTestId(
                  'founder-filter-search',
                  !!searchValue,
                )}>
                <TextField
                  className={cn(
                    classes.searchInput,
                    CLASS_TRACKING.FILTER_ELEMENT,
                  )}
                  value={searchValue}
                  onChange={handleSearch}
                  placeholder='Search'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  small
                />
              </div>
            )}
            {hasAccessToAction('founder.filters.status') && (
              <div
                className={classes.filterItem}
                data-testid={getDataTestId(
                  'founder-filter-status',
                  filterValue?.key === 'status' &&
                  filterValue.value !== filters.status[0].value,
                )}>
                <Select
                  className={cn(
                    classes.filterSelect,
                    CLASS_TRACKING.FILTER_ELEMENT,
                  )}
                  value={
                    filterValue?.key === 'status'
                      ? filterValue.value
                      : filters.status[0].value
                  }
                  options={filters.status}
                  onSelect={(item) => handleFilterUpdate('status', item?.value)}
                  isActive={
                    filterValue?.key === 'status' &&
                    filterValue.value !== filters.status[0].value
                  }
                />
              </div>
            )}
            {!isSmallTablet && hasAccessToAction('founder.filters.venture') && (
              <div
                className={classes.filterItem}
                data-testid={getDataTestId(
                  'founder-filter-venture',
                  filterValue?.key === 'venture',
                )}>
                <SelectVenturePopover
                  className={cn(
                    classes.ventureInput,
                    CLASS_TRACKING.FILTER_ELEMENT,
                  )}
                  onChange={(val) =>
                    handleFilterUpdate('venture', encodeQuery(val))
                  }
                  value={
                    filterValue?.key === 'venture' && filterValue.value
                      ? decodeQuery(filterValue.value)
                      : undefined
                  }
                  isActive={filterValue?.key === 'venture'}
                  small
                />
              </div>
            )}
            {showClearFilter && (
              <Tooltip title='Clear all filters'>
                <IconButton
                  data-testid='button-remove-filter'
                  className={classes.containerRemoveFilter}
                  onClick={() => handleClearFilters()}>
                  <img
                    src='/filter.svg'
                    alt='Remove filter'
                    className={classes.iconRemoveFilter}
                  />
                </IconButton>
              </Tooltip>
            )}
          </div>

          {selectedFounderIds.length > 0 && (
            <div className={classes.filterActions}>
              <Text variant='normal'>{selectedFounderIds.length} selected</Text>
              {hasAccessToAction('founder.details.update') && (
                <Tooltip title='Archive'>
                  <IconButton
                    className={cn(
                      classes.filterActionsAssignArchived,
                      CLASS_TRACKING.INTERNAL_ACTION,
                    )}
                    onClick={() =>
                      setArchiveTargetFounders(
                        statusSelectedFounders?.activeSelectedFounders?.length
                          ? statusSelectedFounders.activeSelectedFounders
                          : null,
                      )
                    }
                    disabled={
                      statusSelectedFounders?.disabledButton === 'archived'
                    }>
                    <ArchiveIcon />
                  </IconButton>
                </Tooltip>
              )}
              {hasAccessToAction('founder.details.update') && (
                <Modal
                  title='Un-Archive founders'
                  caption={`Do you really want to un-archive ${statusSelectedFounders?.archivedSelectedFounders.length
                    } ${statusSelectedFounders?.activeSelectedFounders.length === 1
                      ? 'founder'
                      : 'founders'
                    }?`}
                  contentRenderer={({ handleClose }) => (
                    <ButtonsChangeStatusFounder
                      onCancel={handleClose}
                      selectedValue={
                        statusSelectedFounders?.archivedSelectedFounders
                      }
                      status='Active'
                      updateTable={() => loadFounders(filterValue, page)}
                    />
                  )}
                  buttonRenderer={({ onClick }) => (
                    <Tooltip title='Un-Archive'>
                      <IconButton
                        className={cn(
                          classes.filterActionsAssignUnArchive,
                          CLASS_TRACKING.INTERNAL_ACTION,
                        )}
                        onClick={onClick}
                        disabled={
                          statusSelectedFounders?.disabledButton === 'active'
                        }>
                        <UnarchiveIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  width={350}
                />
              )}
              {hasAccessToAction('founder.updateProfile') && (
                <ConfirmButton
                  title='Send Mass Profile Update Requests'
                  loading={isSendingProfileUpdate}
                  body='Your are about to send a number of temporary links to selected participants, so that they update their profiles. Each link can work only 1 time, and is available for 48 hours only.'
                  successProps={{
                    btnLabel: 'Apply',
                    onSuccess: sendFounderUpdateProfilesSelected,
                  }}
                  cancelProps={{
                    btnLabel: 'Cancel',
                  }}
                  buttonRenderer={({ onClick }) => (
                    <Tooltip title='Send a link to request a profile update'>
                      <IconButton onClick={onClick}>
                        <AccountCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                />
              )}
            </div>
          )}
        </div>
      )}

      <div ref={containerRef}>
        {founders.length > 0 ? (
          <div>
            <Table
              onRowClick={
                hasAccessToAction('founder.details.update')
                  ? handleRowClick
                  : () => { }
              }
              gridClassName={classes.table}
              headerHeight={56}
              height={64 * (founders.length > 10 ? founders.length : 10) + 56}
              rowHeight={64}
              rowCount={founders.length}
              rowGetter={({ index }) => founders[index]}
              rowClassName={classes.row}
              headerRowRenderer={(headRowProps) => (
                <TableHeadRow {...headRowProps} />
              )}
              rowRenderer={(rowProps) => (
                <TableRow
                  data-testid={`founder-table-row-${rowProps.index}`}
                  selected={selectedFounderIds.includes(rowProps.rowData.id)}
                  {...rowProps}
                />
              )}
              width={containerWidth || 500}>
              <Column
                dataKey='id'
                className={classes.column}
                width={
                  !isTablet() && hasAccessToAction('founder.details.update')
                    ? 72
                    : 30
                }
                minWidth={
                  !isTablet() && hasAccessToAction('founder.details.update')
                    ? 72
                    : 30
                }
                headerRenderer={() => (
                  <div>
                    {!isTablet() &&
                      hasAccessToAction('founder.details.update') && (
                        <Checkbox
                          checked={
                            selectedFounderIds.length === founders.length
                          }
                          color='primary'
                          indeterminate={
                            selectedFounderIds.length > 0 &&
                            selectedFounderIds.length !== founders.length
                          }
                          onChange={(e) => handleAllRowSelect(e.target.checked)}
                        />
                      )}
                  </div>
                )}
                cellRenderer={({ cellData }) => (
                  <div>
                    {!isTablet() &&
                      hasAccessToAction('founder.details.update') && (
                        <Checkbox
                          data-testid='founder-table-row-checkbox'
                          checked={selectedFounderIds.includes(cellData)}
                          color='primary'
                          onChange={(e) =>
                            handleRowSelect(e.target.checked, cellData)
                          }
                        />
                      )}
                  </div>
                )}
              />
              {!isMobile() && (
                <Column
                  dataKey='logo'
                  className={classes.column}
                  width={44}
                  minWidth={44}
                  headerRenderer={() => <div></div>}
                  cellRenderer={({ rowData }) => {
                    return (
                      <div>
                        <Avatar
                          src={foundersLogos[rowData.id]}
                          name={`${rowData.firstName[0]}${rowData.lastName[0]}`}
                          size='32'>
                          {typeof foundersLogos[rowData.id] === 'string' &&
                            !foundersLogos[rowData.id] ? (
                            <CircularProgress size={18} color='inherit' />
                          ) : undefined}
                        </Avatar>
                      </div>
                    );
                  }}
                />
              )}
              <Column
                dataKey='name'
                className={cn(classes.column, classes.nameColumn)}
                headerClassName={classes.nameColumn}
                width={172}
                minWidth={172}
                maxWidth={357}
                flexGrow={1}
                headerRenderer={() => <TableHeadCell>Name</TableHeadCell>}
                cellRenderer={({ rowData }) => (
                  <TableCell truncated>
                    <MaterialLink
                      onClick={stopPropagation}
                      data-testid='founder-table-name'
                      component={Link}
                      to={{
                        pathname: getRoutePath(Pages.FOUNDER_DETAILS, {
                          founderId: rowData.id,
                        }),
                        state: {
                          prevPath: currentPath,
                        },
                      }}>
                      {`${rowData.firstName} ${rowData.lastName}`}
                    </MaterialLink>
                  </TableCell>
                )}
              />
              <Column
                dataKey='linkedInProfile'
                className={classes.column}
                width={60}
                minWidth={60}
                maxWidth={76}
                flexGrow={1}
                headerRenderer={() => <div></div>}
                cellRenderer={({ rowData, cellData }) =>
                  cellData ? (
                    <div>
                      <Tooltip
                        title={`${rowData.firstName} ${rowData.lastName}'s LinkedIn profile`}>
                        <IconButton
                          onClick={stopPropagation}
                          target='_blank'
                          href={urlfy(cellData)}
                          className={classes.linkedinButton}>
                          <LinkedInIcon className={classes.linkedinIcon} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  ) : (
                    <div></div>
                  )
                }
              />
              <Column
                dataKey='status'
                className={classes.column}
                width={100}
                minWidth={100}
                maxWidth={120}
                flexGrow={1}
                headerRenderer={() => <TableHeadCell>Status</TableHeadCell>}
                cellRenderer={({ cellData }) => {
                  const currentVariant = getColorStatus(cellData);
                  return (
                    <StatusBadge status={cellData} variant={currentVariant} />
                  );
                }}
              />
              {!isMobile() && (
                <Column
                  dataKey='venture'
                  className={classes.column}
                  width={160}
                  minWidth={isTablet() ? 100 : 200}
                  maxWidth={395}
                  flexGrow={1}
                  headerRenderer={() => <TableHeadCell>Venture</TableHeadCell>}
                  cellRenderer={({ rowData }) => (
                    <TableCell truncated>
                      {rowData.primaryVenture ? (
                        <MaterialLink
                          onClick={stopPropagation}
                          component={Link}
                          to={{
                            pathname: getRoutePath(Pages.VENTURE_DETAILS, {
                              ventureId: rowData.primaryVentureId,
                            }),
                            state: {
                              prevPath: currentPath,
                            },
                          }}>
                          {rowData.primaryVenture}
                        </MaterialLink>
                      ) : (
                        <div>–</div>
                      )}
                    </TableCell>
                  )}
                />
              )}
              {!isTablet() && (
                <Column
                  dataKey='email'
                  className={cn(classes.column, classes.emailColumn)}
                  headerClassName={classes.emailColumn}
                  width={175}
                  minWidth={175}
                  maxWidth={435}
                  flexGrow={1}
                  headerRenderer={() => <TableHeadCell>Email</TableHeadCell>}
                  cellRenderer={({ cellData }) => (
                    <CopyText onClick={stopPropagation} text={cellData}>
                      <TableCell onClick={stopPropagation} truncated>
                        <MaterialLink href={`mailto:${cellData}`}>
                          {cellData}
                        </MaterialLink>
                      </TableCell>
                    </CopyText>
                  )}
                />
              )}
              {!isTablet() && (
                <Column
                  dataKey='about'
                  className={classes.column}
                  width={71}
                  minWidth={71}
                  maxWidth={71}
                  headerRenderer={() => <TableHeadCell>About</TableHeadCell>}
                  cellRenderer={({ rowData }: { rowData: Founder }) => {
                    return (
                      <TableCell className={classes.iconCell}>
                        <AboutIcon
                          about={rowData.about}
                          pathname={getRoutePath(Pages.FOUNDER_DETAILS, {
                            founderId: rowData.id,
                          })}
                        />
                      </TableCell>
                    );
                  }}
                />
              )}
              {!isMobile() && !hasRole(Role.Founder) && !hasRole(Role.Mentor) && (
                <Column
                  dataKey='actions'
                  className={cn(classes.column, classes.justifyContentEnd)}
                  minWidth={40}
                  width={40}
                  headerRenderer={() => <div></div>}
                  cellRenderer={({ rowData }: { rowData: Founder }) => (
                    <TableCell onClick={stopPropagation}>
                      <FoundersRowActions founder={rowData} />
                    </TableCell>
                  )}
                />
              )}
            </Table>
          </div>
        ) : (
          <>
            {isEmptyState ? (
              <div className={classes.emptyBlock}>
                <div className={classes.emptyState}>
                  <Text variant='normal' testid='founder-table-no-items'>
                    This panel contains a list of founders
                    {!!filterValue &&
                      (() => {
                        const filter = getFilterItemByFilter(filterValue);
                        return (
                          <>
                            {' '}
                            filtered by{' '}
                            <Text variant='normal' bold>
                              {filter.label}: {filter.value}
                            </Text>
                          </>
                        );
                      })()}
                    .<br />
                    {hasAccessToAction('founder.create') &&
                      'We were not able to find information you requested, but feel free to add something new!'}
                  </Text>
                  {hasAccessToAction('founder.create') && (
                    <MaterialButton
                      component={Link}
                      to={Pages.NEW_FOUNDER}
                      className={classes.emptyStateBtn}
                      startIcon={<AddIcon />}
                      variant='contained'
                      color='primary'>
                      Add Founder
                    </MaterialButton>
                  )}
                </div>
              </div>
            ) : (
              <div className={classes.emptyBlock}>
                {isLoading && <CircularProgress size={36} color='primary' />}
                {!isLoading && !!loadError && (
                  <AlertState type='error'>{loadError}</AlertState>
                )}
              </div>
            )}
          </>
        )}
        {!!perPage && (
          <TableFooter
            page={page}
            onPageChange={handlePageChange}
            disabled={isLoading}
            isLoading={isNextPageLoading}
            hasNextPage={founders.length > 0 && hasNextPage}
          />
        )}
      </div>
    </Paper>
  );
}

export default FoundersTable;
